import type { AuthTokens, BudgetUser } from '../../../types/auth';
import { apiRequest } from '../../../services/apiClient';

export type SendOTPResponse = {
  message: string;
  attemptsRemaining?: number;
  resetAt?: number;
};

export type AuthResponse = {
  token: string;
  refreshToken: string;
  isNewUser?: boolean;
  user: BudgetUser;
};

export type ProfileResponse = {
  user: BudgetUser;
};

class AuthService {
  async sendOTP(phoneNumber: string, deviceId?: string): Promise<SendOTPResponse> {
    return apiRequest<SendOTPResponse>('/auth/otp/send', {
      method: 'POST',
      body: JSON.stringify({ phoneNumber, deviceId }),
    });
  }

  async verifyOTP(phoneNumber: string, otp: string, deviceId?: string): Promise<AuthResponse> {
    return apiRequest<AuthResponse>('/auth/otp/verify', {
      method: 'POST',
      body: JSON.stringify({ phoneNumber, otp, deviceId }),
    });
  }

  async refreshToken(refreshToken: string): Promise<{ token: string; refreshToken: string }> {
    return apiRequest<{ token: string; refreshToken: string }>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ token: refreshToken }),
      skipAuthRefresh: true,
    });
  }

  async googleAuth(idToken: string): Promise<AuthResponse> {
    return apiRequest<AuthResponse>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ idToken }),
      skipAuthRefresh: true,
    });
  }

  /** Sign in with Apple (iOS + web) — server verifies the identity token against Apple JWKS. */
  async appleAuth(params: {
    identityToken: string;
    authorizationCode?: string;
    user?: string;
    email?: string;
    fullName?: string;
  }): Promise<AuthResponse> {
    return apiRequest<AuthResponse>('/auth/apple', {
      method: 'POST',
      body: JSON.stringify(params),
      skipAuthRefresh: true,
    });
  }

  /** Web: server exchanges auth code with client_secret (PKCE verifier optional). */
  async googleAuthWithCode(params: {
    code: string;
    redirectUri: string;
    codeVerifier?: string;
  }): Promise<AuthResponse> {
    return apiRequest<AuthResponse>('/auth/google', {
      method: 'POST',
      body: JSON.stringify(params),
      skipAuthRefresh: true,
    });
  }

  async getProfile(accessToken: string): Promise<ProfileResponse> {
    return apiRequest<ProfileResponse>('/auth/profile', {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  }

  async updateProfile(fields: {
    name?: string;
    picture?: string;
    onboardingComplete?: boolean;
    monthlyIncome?: number;
    birthYear?: number;
    retirementAge?: number;
    assumedReturnPct?: number;
  }): Promise<ProfileResponse> {
    return apiRequest<ProfileResponse>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(fields),
    });
  }

  /** Soft-delete: deactivates sign-in. Associated records are kept. */
  async deleteAccount(): Promise<{ ok: boolean; alreadyDeleted?: boolean }> {
    return apiRequest<{ ok: boolean; alreadyDeleted?: boolean }>('/auth/account', {
      method: 'DELETE',
    });
  }
}

export const authService = new AuthService();

export function toAuthTokens(response: AuthResponse): AuthTokens {
  return {
    accessToken: response.token,
    refreshToken: response.refreshToken,
  };
}
